/** @jsxImportSource @emotion/react */
import { Icon, MenuItem, Tooltip } from "@blueprintjs/core"

import { ItemRendererProps, Suggest } from "@blueprintjs/select"
import { SerializedStyles } from "@emotion/react"
import { useState, useEffect } from "react"
import { UseControllerProps, useController } from "react-hook-form"
import { useDebounce } from "../../../hooks/useDebounce"
import useTradingInstruments from "../hooks/useTradingInstruments"
import { InstrumentLight } from "../types"

interface TradingSuggestProps extends UseControllerProps<any> {
  customCss?: SerializedStyles
  withPriceUsd?: boolean
  onChange?: () => void
  initialInstrumentQuery?: string
}

const InstrumentSuggest = ({
  control,
  name,
  rules,
  customCss,
  withPriceUsd,
  onChange,
  initialInstrumentQuery,
}: TradingSuggestProps) => {
  const [query, setQuery] = useState(initialInstrumentQuery || "")
  const { instruments, isLoading, error } = useTradingInstruments({ query, withPriceUsd })
  const { field } = useController({ control, name, rules })

  // Effect to handle single match from initial query
  useEffect(() => {
    if (initialInstrumentQuery && !isLoading && instruments?.length === 1 && !field.value) {
      handleItemSelect(instruments[0])
    }
  }, [instruments, initialInstrumentQuery, field, isLoading, onChange])

  // Effect to reset query when field value is cleared (form reset)
  useEffect(() => {
    if (!field.value && !initialInstrumentQuery) {
      setQuery("")
    }
  }, [field.value])

  const debouncedSetQuery = useDebounce((newQuery: string) => {
    if (newQuery.length >= 2 || newQuery.length === 0) {
      setQuery(newQuery)
    }
  }, 300)

  // Render functions for Suggest
  const renderItem = (
    instrument: InstrumentLight,
    { handleClick, modifiers }: ItemRendererProps,
  ) => {
    if (!modifiers.matchesPredicate) {
      return null
    }

    // Create warning icon with tooltip for items outside universe
    const warningIcon = !instrument.in_user_universe ? (
      <Tooltip content="This instrument is outside your universe">
        <Icon icon="warning-sign" intent="warning" />
      </Tooltip>
    ) : undefined

    return (
      <MenuItem
        active={modifiers.active}
        key={instrument.id}
        onClick={handleClick}
        text={instrument.bloomberg_code}
        icon={warningIcon}
        intent={!instrument.in_user_universe ? "warning" : undefined}
      />
    )
  }

  const renderInputValue = (instrument: InstrumentLight) => instrument.bloomberg_code

  /**
   * Handle tab key to select first instrument
   * Only allow selection when not loading and query is complete
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab" && (instruments?.length ?? 0) > 0) {
      handleItemSelect(instruments![0])
    }
  }

  const handleItemSelect = (item: InstrumentLight) => {
    field.onChange(item)
    setQuery(item.bloomberg_code)
    if (onChange) onChange()
  }

  return (
    <Suggest
      items={instruments || []}
      itemRenderer={renderItem}
      inputValueRenderer={renderInputValue}
      onItemSelect={handleItemSelect}
      onQueryChange={newQuery => {
        debouncedSetQuery(newQuery)
      }}
      query={query}
      selectedItem={field.value}
      noResults={
        <MenuItem
          disabled={true}
          text={
            query.length < 2
              ? "Type to search"
              : isLoading
                ? "Loading..."
                : error
                  ? "Error loading results"
                  : "No results found"
          }
        />
      }
      resetOnSelect={true}
      resetOnQuery={true}
      resetOnClose={true}
      popoverProps={{ minimal: true }}
      css={customCss}
      inputProps={{
        inputRef: field.ref,
        onKeyDown: handleKeyDown,
      }}
    />
  )
}

export default InstrumentSuggest
